import { BroadcastMessageType, type BroadcastMessage } from '@core/types'
import { ref, useTimeoutFn, useSharedBroadcast, useRouter } from '#imports'

/**
 * Guards against opening more than one game at a time. `openingSlug` holds the
 * slug of the game being opened, shared across every game tile, winner tile and
 * hero rotator. While it is non-null, a click on any tile is short-circuited at
 * the call site before it can emit a second `game-click`.
 *
 * Why suppress rather than dedupe: a second tile must not start a second game
 * or show two tiles in the loading state at once. Consumers also compare
 * `openingSlug` to their own slug to scope loading visuals to the clicked tile.
 *
 * Released by (in order of reliability): navigating to any non-playgame route
 * or to a playgame route whose `start` query is the game being opened
 * (`afterEach`, covers tiles unmounting before the broadcast arrives), the
 * `GAME_TILE_STOP_LOADING` broadcast (e.g. a bonus modal, no navigation), or a
 * 5s timeout armed via `startOpeningTimeout()` in case `start()` throws.
 */
const openingSlug = ref<string | null>(null)

let routerHookRegistered = false

export const useGameOpeningGuard = () => {
    let startOpeningTimeout: () => void = () => {}

    if (import.meta.client) {
        // Register the navigation reset once, decoupled from any tile's
        // lifecycle so it survives tiles unmounting/remounting.
        if (!routerHookRegistered) {
            routerHookRegistered = true
            useRouter().afterEach((to) => {
                if (!to.meta?.isPlaygame) {
                    openingSlug.value = null

                    return
                }

                if (to.query?.start === openingSlug.value) openingSlug.value = null
            })
        }

        const _timeout = useTimeoutFn(() => {
            openingSlug.value = null
        }, 5_000, { immediate: false })
        startOpeningTimeout = _timeout.start

        const { onMessage } = useSharedBroadcast()
        onMessage((message: BroadcastMessage) => {
            if (message?.type !== BroadcastMessageType.GAME_TILE_STOP_LOADING) return
            openingSlug.value = null
        })
    }

    return { openingSlug, startOpeningTimeout }
}
